import type { Task } from "../data/blocks";
import { PILLAR_DESCRIPTIONS, PILLAR_META } from "../data/pillars";
import { T } from "../styles/theme";
import type { CheckedMap } from "../types";
import { Checkmark } from "./Checkmark";

interface PillarViewProps {
  pillar: string;
  tasks: Task[];
  checked: CheckedMap;
  onToggle: (id: string) => void;
}

/** Pillar-filtered view: a header card + tasks grouped by category. */
export function PillarView({ pillar, tasks, checked, onToggle }: PillarViewProps) {
  const grouped: Record<string, Task[]> = {};
  for (const t of tasks) {
    (grouped[t.cat] = grouped[t.cat] ?? []).push(t);
  }
  const pd = PILLAR_DESCRIPTIONS[pillar];
  const entries = Object.entries(grouped);

  return (
    <>
      {pd && (
        <div
          style={{
            background: `linear-gradient(135deg,${pd.color},${T.white})`,
            border: `1.5px solid ${pd.accent}44`,
            borderRadius: 18,
            padding: "16px 18px",
            marginBottom: 14,
            boxShadow: `0 3px 16px ${pd.accent}18`,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <span style={{ fontSize: 30, lineHeight: 1 }}>{pd.emoji}</span>
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: T.ink,
                  fontFamily: T.display,
                  letterSpacing: -0.2,
                }}
              >
                {pd.title}
              </div>
              <div style={{ fontSize: 12, color: pd.accent, fontStyle: "italic", marginTop: 4, lineHeight: 1.55 }}>
                {pd.desc}
              </div>
            </div>
          </div>
        </div>
      )}

      {entries.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "32px 16px",
            color: T.inkLight,
            fontStyle: "italic",
            fontSize: 13,
          }}
        >
          Nothing in this pillar today ✨ Enjoy the space.
        </div>
      )}

      {entries.map(([cat, catTasks]) => {
        const meta = PILLAR_META[cat] ?? { bg: T.parchment, accent: T.terra };
        const catDone = catTasks.filter((t) => t.id && checked[t.id]).length;
        return (
          <div
            key={cat}
            style={{
              background: T.white,
              borderRadius: 16,
              marginBottom: 10,
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(45,32,22,0.06)",
              border: `1px solid ${meta.accent}22`,
            }}
          >
            <div
              style={{
                padding: "9px 16px",
                background: meta.bg,
                borderBottom: `1px solid ${meta.accent}22`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 700, color: meta.accent, letterSpacing: 0.3 }}>{cat}</span>
              <span
                style={{
                  fontSize: 10,
                  color: meta.accent,
                  background: "rgba(255,255,255,0.65)",
                  borderRadius: 99,
                  padding: "2px 8px",
                }}
              >
                {catDone}/{catTasks.length}
              </span>
            </div>
            {catTasks.map((task, i) => {
              const id = task.id ?? "";
              const isDone = !!checked[id];
              return (
                <div
                  key={id}
                  onClick={() => onToggle(id)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    padding: "12px 16px",
                    gap: 11,
                    cursor: "pointer",
                    borderBottom: i < catTasks.length - 1 ? `1px solid ${T.cream}` : "none",
                    background: isDone ? "#f8fdf6" : T.white,
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      flexShrink: 0,
                      marginTop: 1,
                      border: isDone ? "none" : `2px solid ${meta.accent}`,
                      background: isDone ? T.sage : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    {isDone && <Checkmark />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: isDone ? T.sageLight : T.ink,
                        textDecoration: isDone ? "line-through" : "none",
                        transition: "all 0.2s",
                        lineHeight: 1.45,
                      }}
                    >
                      {task.label}
                    </div>
                    <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontSize: 9,
                          color: T.inkLight,
                          background: T.parchment,
                          borderRadius: 6,
                          padding: "2px 7px",
                          fontStyle: "italic",
                        }}
                      >
                        🕐 {task.time}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          color: T.inkLight,
                          background: T.parchment,
                          borderRadius: 6,
                          padding: "2px 7px",
                          fontStyle: "italic",
                        }}
                      >
                        ⏱ {task.duration}
                      </span>
                      {task.personal && (
                        <span
                          style={{
                            fontSize: 9,
                            color: T.terra,
                            background: `${T.terra}15`,
                            borderRadius: 6,
                            padding: "2px 7px",
                          }}
                        >
                          🌟 yours
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
